import JsBarcode from "jsbarcode";
import logoUrl from "images/logo.svg";

/** 99×70mm at ~254 DPI for crisp print */
const BADGE_WIDTH = 990;
const BADGE_HEIGHT = 700;
const HEADER_HEIGHT = 88;
const BLACK = "#000000";
const WHITE = "#FFFFFF";
const SIDE_PAD = 48;
const KNOWN_PREFIXES = ["FL", "TL", "TR", "EQ"] as const;

export type EquipmentDisplayPrefix = typeof KNOWN_PREFIXES[number];

export interface EquipmentBadgeInput {
  unitNumber: string;
  equipmentType: string;
  terminalLabel?: string;
}

export function getEquipmentDisplayPrefix(equipmentType: string | null | undefined): EquipmentDisplayPrefix {
  const normalized = (equipmentType || "").trim().toLowerCase();
  if (normalized.includes("forklift")) return "FL";
  if (normalized.includes("trailer")) return "TL";
  if (normalized.includes("tractor") || normalized.includes("power")) return "TR";
  return "EQ";
}

export function stripEquipmentDisplayPrefix(unitNumber: string): string {
  return String(unitNumber)
    .trim()
    .replace(/^(FL|TL|TR|EQ)-?/i, "")
    .trim();
}

/** Display-only ID for the badge (barcode uses raw unitNumber). */
export function formatEquipmentDisplayId(
  unitNumber: string,
  equipmentType: string | null | undefined
): string {
  const digits = stripEquipmentDisplayPrefix(unitNumber);
  if (!digits) return "";
  return `${getEquipmentDisplayPrefix(equipmentType)}-${digits}`;
}

export function formatEquipmentTerminalLabel(terminal: {
  airportCode?: string | null;
  terminalName?: string | null;
  terminalNumber?: number | string | null;
}): string {
  const airportCode = (terminal.airportCode || "").trim();
  const terminalName = (terminal.terminalName || "").trim();
  const terminalNumber =
    terminal.terminalNumber !== null && terminal.terminalNumber !== undefined
      ? String(terminal.terminalNumber).trim()
      : "";

  if (!airportCode || !terminalName) return terminalName || "";
  return terminalNumber
    ? `${airportCode}-${terminalName} ${terminalNumber}`
    : `${airportCode}-${terminalName}`;
}

function sanitizeFilename(value: string): string {
  return value.replace(/[/\\?%*:|"<>]/g, "_");
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load logo"));
    img.src = src;
  });
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/** Draw logo as solid white silhouette on the black header. */
function drawWhiteLogo(
  ctx: CanvasRenderingContext2D,
  logo: HTMLImageElement,
  x: number,
  y: number,
  width: number,
  height: number
): void {
  const offscreen = document.createElement("canvas");
  offscreen.width = Math.ceil(width);
  offscreen.height = Math.ceil(height);
  const offCtx = offscreen.getContext("2d");
  if (!offCtx) return;

  offCtx.drawImage(logo, 0, 0, width, height);
  offCtx.globalCompositeOperation = "source-in";
  offCtx.fillStyle = WHITE;
  offCtx.fillRect(0, 0, width, height);
  ctx.drawImage(offscreen, x, y);
}

/** Wide-tracked uppercase label (Figma-style letter spacing). */
function drawTrackedText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  trackingPx: number
): void {
  const chars = text.split("");
  const widths = chars.map((ch) => ctx.measureText(ch).width);
  const total =
    widths.reduce((sum, w) => sum + w, 0) + trackingPx * Math.max(chars.length - 1, 0);
  let cursor = x - total / 2;
  ctx.textAlign = "left";
  chars.forEach((ch, i) => {
    ctx.fillText(ch, cursor, y);
    cursor += widths[i] + trackingPx;
  });
  ctx.textAlign = "center";
}

function drawHorizontalRule(ctx: CanvasRenderingContext2D, y: number): void {
  ctx.strokeStyle = BLACK;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(SIDE_PAD, y);
  ctx.lineTo(BADGE_WIDTH - SIDE_PAD, y);
  ctx.stroke();
}

function drawTypeChip(
  ctx: CanvasRenderingContext2D,
  typeLabel: string,
  headerHeight: number
): void {
  ctx.font = "bold 20px Arial, Helvetica, sans-serif";
  const textWidth = ctx.measureText(typeLabel).width;
  const padX = 16;
  const padY = 10;
  const chipWidth = textWidth + padX * 2;
  const chipHeight = 22 + padY * 2;
  const chipX = BADGE_WIDTH - SIDE_PAD - chipWidth;
  const chipY = (headerHeight - chipHeight) / 2;

  ctx.strokeStyle = WHITE;
  ctx.lineWidth = 2.5;
  ctx.strokeRect(chipX + 1, chipY + 1, chipWidth - 2, chipHeight - 2);

  ctx.fillStyle = WHITE;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(typeLabel, chipX + chipWidth / 2, chipY + chipHeight / 2 + 1);
}

function renderBarcode(rawUnitNumber: string, maxWidth: number): HTMLCanvasElement {
  const barcodeCanvas = document.createElement("canvas");
  JsBarcode(barcodeCanvas, rawUnitNumber, {
    format: "CODE128",
    displayValue: true,
    font: "Arial",
    fontSize: 24,
    textMargin: 10,
    height: 150,
    margin: 4,
    width: 3.2,
    background: WHITE,
    lineColor: BLACK,
  });

  if (barcodeCanvas.width <= maxWidth) {
    return barcodeCanvas;
  }

  const scaled = document.createElement("canvas");
  const scale = maxWidth / barcodeCanvas.width;
  scaled.width = maxWidth;
  scaled.height = Math.round(barcodeCanvas.height * scale);
  const scaledCtx = scaled.getContext("2d");
  if (scaledCtx) {
    scaledCtx.imageSmoothingEnabled = false;
    scaledCtx.drawImage(barcodeCanvas, 0, 0, scaled.width, scaled.height);
  }
  return scaled;
}

export async function downloadEquipmentBadge(input: EquipmentBadgeInput): Promise<void> {
  const unitNumber = input.unitNumber?.trim();
  if (!unitNumber) {
    throw new Error("Unit number is required");
  }

  const equipmentType = input.equipmentType?.trim() || "";
  const typeLabel = equipmentType ? equipmentType.toUpperCase() : "EQUIPMENT";
  const displayId = formatEquipmentDisplayId(unitNumber, equipmentType) || unitNumber;
  const barcodeValue = stripEquipmentDisplayPrefix(unitNumber) || unitNumber;
  const terminalLabel = input.terminalLabel?.trim() || "";

  const canvas = document.createElement("canvas");
  canvas.width = BADGE_WIDTH;
  canvas.height = BADGE_HEIGHT;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Unable to create canvas context");
  }

  // Body
  ctx.fillStyle = WHITE;
  ctx.fillRect(0, 0, BADGE_WIDTH, BADGE_HEIGHT);

  // Header
  ctx.fillStyle = BLACK;
  ctx.fillRect(0, 0, BADGE_WIDTH, HEADER_HEIGHT);

  const logo = await loadImage(logoUrl);
  const maxLogoWidth = 140;
  const maxLogoHeight = 52;
  const logoScale = Math.min(maxLogoWidth / logo.width, maxLogoHeight / logo.height);
  const logoWidth = logo.width * logoScale;
  const logoHeight = logo.height * logoScale;
  const logoX = SIDE_PAD;
  const logoY = (HEADER_HEIGHT - logoHeight) / 2;
  drawWhiteLogo(ctx, logo, logoX, logoY, logoWidth, logoHeight);

  drawTypeChip(ctx, typeLabel, HEADER_HEIGHT);

  /*
   * Figma proportions (body below header):
   *  - ID block ~34% of body
   *  - Terminal band ~18% (collapsed when missing)
   *  - Barcode block remainder (larger)
   */
  const bodyTop = HEADER_HEIGHT;
  const bodyHeight = BADGE_HEIGHT - HEADER_HEIGHT;
  const hasTerminal = Boolean(terminalLabel);
  const terminalBandHeight = hasTerminal ? Math.round(bodyHeight * 0.18) : 0;
  const idBlockBottom = bodyTop + Math.round(bodyHeight * (hasTerminal ? 0.34 : 0.42));
  const terminalTop = idBlockBottom;
  const terminalBottom = terminalTop + terminalBandHeight;
  const barcodeTop = terminalBottom;

  // EQUIPMENT ID label + large display ID (centered in ID block)
  ctx.fillStyle = BLACK;
  ctx.textBaseline = "top";
  ctx.font = "bold 16px Arial, Helvetica, sans-serif";
  const labelY = bodyTop + 28;
  drawTrackedText(ctx, "EQUIPMENT ID", BADGE_WIDTH / 2, labelY, 5);

  ctx.font = "900 92px Arial Black, Arial, Helvetica, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText(displayId, BADGE_WIDTH / 2, labelY + 32);

  // Rule under ID
  drawHorizontalRule(ctx, idBlockBottom);

  // Terminal — larger so it cannot be skipped
  if (hasTerminal) {
    ctx.font = "bold 44px Arial, Helvetica, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(terminalLabel, BADGE_WIDTH / 2, (terminalTop + terminalBottom) / 2);
    drawHorizontalRule(ctx, terminalBottom);
  }

  // Barcode — dominant bottom element (~80% width)
  const maxBarcodeWidth = BADGE_WIDTH * 0.8;
  const barcodeCanvas = renderBarcode(barcodeValue, maxBarcodeWidth);
  const barcodeAreaHeight = BADGE_HEIGHT - barcodeTop - 20;
  const barcodeScale = Math.min(
    1,
    maxBarcodeWidth / barcodeCanvas.width,
    barcodeAreaHeight / barcodeCanvas.height
  );
  const drawW = barcodeCanvas.width * barcodeScale;
  const drawH = barcodeCanvas.height * barcodeScale;
  const barcodeX = (BADGE_WIDTH - drawW) / 2;
  const barcodeY = barcodeTop + (barcodeAreaHeight - drawH) / 2;
  ctx.drawImage(barcodeCanvas, barcodeX, barcodeY, drawW, drawH);

  // Outer border
  ctx.strokeStyle = BLACK;
  ctx.lineWidth = 3;
  ctx.strokeRect(1.5, 1.5, BADGE_WIDTH - 3, BADGE_HEIGHT - 3);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Failed to generate badge image"));
        return;
      }
      downloadBlob(blob, `${sanitizeFilename(displayId)}_badge.png`);
      resolve();
    }, "image/png");
  });
}
